import React, { useState, useEffect, useCallback } from 'react';
import { useConfirm } from '../common/ConfirmProvider';
import styled from 'styled-components';
import { FiEdit2, FiTrash2, FiUpload, FiAlertCircle, FiLoader } from 'react-icons/fi';
import apiService from '../../services/api';
import logger from '../../utils/logger';
import CSVScoreUpload from './CSVScoreUpload';
import EditExternalItemModal from './EditExternalItemModal';

const Container = styled.div`
  margin: 2rem 0;
`;

const Title = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1.5rem 0;
`;

const TableWrapper = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 12px;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    background: var(--background-secondary);
    border-bottom: 2px solid var(--border-color);
  }

  th {
    padding: 1rem;
    text-align: left;
    font-weight: 600;
    color: var(--text-primary);
    font-size: 0.9rem;
  }

  tbody tr {
    border-bottom: 1px solid var(--border-color);
    transition: background 0.2s ease;

    &:hover {
      background: var(--background-secondary);
    }

    &:last-child {
      border-bottom: none;
    }
  }

  td {
    padding: 1rem;
    color: var(--text-primary);
    font-size: 0.95rem;
  }

  .type {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 6px;
    font-size: 0.85rem;
    font-weight: 600;
    background: var(--background-secondary);
  }

  .type.assignment {
    color: #2196f3;
    background: rgba(33, 150, 243, 0.1);
  }

  .type.quiz {
    color: #9c27b0;
    background: rgba(156, 39, 176, 0.1);
  }

  .external-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    background: rgba(255, 152, 0, 0.1);
    color: #ff9800;
    margin-left: 0.5rem;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 0.5rem;
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
    background: var(--background-secondary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.danger:hover {
    border-color: #f44336;
    color: #f44336;
    background: rgba(244, 67, 54, 0.05);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-secondary);

  p {
    margin: 0;
    font-size: 0.95rem;
  }
`;

const ErrorBox = styled.div`
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid #f44336;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  color: #f44336;
  display: flex;
  gap: 0.75rem;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const SuccessBox = styled(ErrorBox)`
  background: rgba(76, 175, 80, 0.1);
  border-color: #4caf50;
  color: #4caf50;
`;

const ExternalItemsManager = ({ courseId }) => {
  const confirm = useConfirm();
  const [items, setItems] = useState({ assignments: [], quizzes: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [uploadingItem, setUploadingItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getExternalItemsForCourse(courseId);

      if (result.success || result.data) {
        const data = result.data || result;
        setItems(data);
      } else {
        setError('Failed to load external items');
      }
    } catch (err) {
      logger.error('Error loading external items', err);
      setError(err.message || 'Failed to load external items');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    loadItems();
  }, [courseId, loadItems]);

  const handleDelete = async (id, type) => {
    if (!(await confirm({ title: 'Delete item?', message: `Delete this ${type}? This action cannot be undone.`, confirmText: 'Delete', danger: true }))) {
      return;
    }

    try {
      setDeleting(id);
      setError(null);

      const deleteMethod = type === 'assignment'
        ? apiService.deleteExternalAssignment
        : apiService.deleteExternalQuiz;

      const result = await deleteMethod.call(apiService, id, courseId);

      if (result.success) {
        setSuccess(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully`);
        await loadItems();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.message || `Failed to delete ${type}`);
      }
    } catch (err) {
      logger.error(`Error deleting ${type}`, err);
      setError(err.message || `Failed to delete ${type}`);
    } finally {
      setDeleting(null);
    }
  };

  const allItems = [
    ...items.assignments.map(a => ({ ...a, type: 'assignment' })),
    ...items.quizzes.map(q => ({ ...q, type: 'quiz' }))
  ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  if (uploadingItem) {
    return (
      <CSVScoreUpload
        itemId={uploadingItem.id}
        itemType={uploadingItem.type}
        courseId={courseId}
        itemTitle={uploadingItem.title}
        onUploadComplete={() => {
          setUploadingItem(null);
          setSuccess('Scores uploaded successfully');
          setTimeout(() => setSuccess(null), 3000);
          loadItems();
        }}
        onClose={() => setUploadingItem(null)}
      />
    );
  }

  if (editingItem) {
    return (
      <EditExternalItemModal
        item={editingItem}
        itemType={editingItem.type}
        courseId={courseId}
        onClose={() => setEditingItem(null)}
        onSuccess={() => {
          setEditingItem(null);
          setSuccess('Item updated successfully');
          setTimeout(() => setSuccess(null), 3000);
          loadItems();
        }}
      />
    );
  }

  return (
    <Container>
      <Title>External Items</Title>

      {error && (
        <ErrorBox>
          <FiAlertCircle size={20} />
          <span>{error}</span>
        </ErrorBox>
      )}

      {success && (
        <SuccessBox>
          <span>{success}</span>
        </SuccessBox>
      )}

      {loading ? (
        <EmptyState>
          <FiLoader size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '1rem' }} />
          <p>Loading external items...</p>
        </EmptyState>
      ) : allItems.length === 0 ? (
        <EmptyState>
          <p>No external items created yet.</p>
          <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
            Create external items from the Gradebook toolbar to get started.
          </p>
        </EmptyState>
      ) : (
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Points</th>
                <th>Due Date</th>
                <th>Source</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {allItems.map(item => (
                <tr key={`${item.type}-${item.id}`}>
                  <td>
                    <strong>{item.title}</strong>
                    {item.description && (
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                        {item.description.substring(0, 50)}
                        {item.description.length > 50 ? '...' : ''}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`type ${item.type}`}>
                      {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                    </span>
                    <span className="external-badge">External</span>
                  </td>
                  <td>{item.points || 'N/A'}</td>
                  <td>
                    {item.dueDate || item.dueAt
                      ? new Date(item.dueDate || item.dueAt).toLocaleDateString()
                      : 'No due date'}
                  </td>
                  <td>
                    {item.externalSourceName || 'Unknown'}
                  </td>
                  <td>
                    <Actions>
                      <ActionButton
                        title="Upload/Update Scores"
                        onClick={() => setUploadingItem(item)}
                        disabled={deleting}
                      >
                        <FiUpload size={18} />
                      </ActionButton>
                      <ActionButton
                        title="Edit"
                        onClick={() => setEditingItem(item)}
                        disabled={deleting}
                      >
                        <FiEdit2 size={18} />
                      </ActionButton>
                      <ActionButton
                        className="danger"
                        title="Delete"
                        onClick={() => handleDelete(item.id, item.type)}
                        disabled={deleting !== null}
                      >
                        {deleting === item.id ? (
                          <FiLoader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                        ) : (
                          <FiTrash2 size={18} />
                        )}
                      </ActionButton>
                    </Actions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      )}
    </Container>
  );
};

export default ExternalItemsManager;
